import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import Modal from '@/components/shared/Modal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

import {
    Upload as UploadIcon,
    ScreenShare as ScreenShareIcon,
    ScreenShareOff,
    SwitchCamera as SwitchCameraIcon,
    Camera as CameraIcon,
    Video as VideoCameraIcon,
    Check as CheckIcon,
    Download as DownloadIcon,
    FlipHorizontal2 as FlipIcon,
    ImagePlus as PhotoIcon,
    RotateCcw as RefreshIcon,
    Square as StopSquare,
} from 'lucide-react';
import PlaybackControls from './PlaybackControls';
import { useSettings } from './SettingsContext';
import { cn } from '@/lib/utils';
import { useCameraDevices, type CameraDeviceOption } from '@/hooks/use-camera-devices';
import { useGoniometers, type GoniometerInstance } from '@/hooks/use-goniometers';
import { PASSIVE_TEST_DATA } from '@/components/sop-intake/constants';
import type { TestData } from '@/components/sop-intake/types';
import type { FormValues } from '@/lib/types';
import logger, { LogCategory } from '@/lib/logging';
import { captureSourceForMedia, confirmMeasurement, type ValueConfirm } from './measurement-confirm';

interface VisioMeter2Props {
    isOpen: boolean;
    onClose: () => void;
    formValues: FormValues;
    onValueConfirm: ValueConfirm;
    onValueChange?: (testKey: string, side: 'L' | 'R', value: number) => void;
    initialTest?: { testKey: string; side: 'L' | 'R' };
    testDataMap?: TestData;
    /** Used to name exported measurement images; client.name is optional in practice. */
    clientName?: string;
    /**
     * Remote Assessment: shared call window to measure on. VGon works on its own clones, so
     * capture/freeze (which stops tracks) never ends the practitioner's screen share.
     */
    videoSourceStream?: MediaStream | null;
    /** Render inline (Remote Assessment workbench) instead of in a full-screen modal. */
    embedded?: boolean;
    /** Extra controls under the values panel (Remote Assessment: active SoP table + save). */
    sidebarExtra?: React.ReactNode;
    /** Layer drawn over the media pane, given the live <video> (Remote Assessment: live landmarks). */
    renderMediaOverlay?: (video: HTMLVideoElement | null) => React.ReactNode;
}

type Point = { x: number; y: number };
type GoniometerState = { pivot: Point; arm1: Point; arm2: Point };
type ActiveHandle = 'pivot' | 'arm1' | 'arm2' | null;
type MediaType = 'camera' | 'screen' | 'image' | 'video';

const getCameraErrorMessage = (err: unknown): string => {
    const errorName = err && typeof err === 'object' && 'name' in err ? String((err as { name?: unknown }).name) : '';
    if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
        return 'Camera permission denied. Allow camera access and retry.';
    }
    if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
        return 'No camera device was found.';
    }
    if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
        return 'Camera is unavailable or in use by another app.';
    }
    if (errorName === 'OverconstrainedError' || errorName === 'ConstraintNotSatisfiedError') {
        return 'Selected camera is unavailable. Try switching cameras.';
    }
    return 'Camera access denied or unavailable.';
};

const calculateAngle = (p: Point, p1: Point, p2: Point) => {
    const angle1 = Math.atan2(p1.y - p.y, p1.x - p.x);
    const angle2 = Math.atan2(p2.y - p.y, p2.x - p.x);
    let angle = (angle2 - angle1) * 180 / Math.PI;
    if (angle < 0) angle += 360;
    // Return the smaller angle (medical goniometry standard)
    return Math.min(angle, 360 - angle);
};

// Pixel-accurate angle calculation that compensates for non-square overlay aspect ratios
const calculateAnglePx = (p: Point, p1: Point, p2: Point, width: number, height: number) => {
    const ax = (p1.x - p.x) * width;
    const ay = (p1.y - p.y) * height;
    const bx = (p2.x - p.x) * width;
    const by = (p2.y - p.y) * height;

    const angle1 = Math.atan2(ay, ax);
    const angle2 = Math.atan2(by, bx);
    let angle = (angle2 - angle1) * 180 / Math.PI;
    if (angle < 0) angle += 360;
    return Math.min(angle, 360 - angle);
};

const HANDLE_RADIUS = 15;
const FRAME_STEP_AMOUNT = 1 / 30;

// Camera-picker availability indicators: green = usable, red = failed this session
// (in use elsewhere / broken), orange = needs permission (selecting it triggers the prompt)
const cameraDotClass = (availability: CameraDeviceOption['availability']): string => {
    if (availability === 'unavailable') return 'bg-error';
    if (availability === 'needs-permission') return 'bg-warning';
    return 'bg-success';
};
const cameraAvailabilityText = (availability: CameraDeviceOption['availability']): string => {
    if (availability === 'unavailable') return ' (unavailable)';
    if (availability === 'needs-permission') return ' (needs permission)';
    return '';
};

// Single source of truth for "this element owns its own click/key semantics" — used by BOTH
// the click-to-pause guard and the keyboard-shortcut guard so they can never drift apart.
const INTERACTIVE_ELEMENT_SELECTOR =
    'button, input, textarea, select, label, a, [contenteditable="true"], [role="combobox"], [role="listbox"], [role="option"], [role="slider"], [role="switch"]';

// Arrow keys only defer to controls that natively CONSUME arrows (text cursors, dropdown
// navigation, slider nudging) — a focused plain button does not, so frame-stepping keeps
// working right after clicking Play/Step/Flip (previously the full interactive guard made
// the arrows feel dead any time a button held focus).
const ARROW_KEY_CONSUMER_SELECTOR =
    'input, textarea, select, [contenteditable="true"], [role="combobox"], [role="listbox"], [role="option"], [role="slider"]';

type GoniometerHandle = Exclude<ActiveHandle, null>;

interface GoniometerOverlayProps {
    goniometer: GoniometerState;
    angle: number;
    color: string;
    pivotColor: string;
    thickness: number;
    onNudge: (handle: GoniometerHandle, dx: number, dy: number) => void;
    /** Dual-goniometer tests label each instance (L/R leg) on screen and in aria names. */
    label?: 'L' | 'R';
}

// Shared between the desktop and mobile layouts; handles are keyboard-operable (role=slider, arrow keys).
const GoniometerOverlay: React.FC<GoniometerOverlayProps> = ({ goniometer, angle, color, pivotColor, thickness, onNudge, label }) => {
    const makeKeyHandler = (handle: GoniometerHandle) => (e: React.KeyboardEvent<SVGCircleElement>) => {
        const step = e.shiftKey ? 0.05 : 0.01;
        let dx = 0;
        let dy = 0;
        if (e.key === 'ArrowLeft') dx = -step;
        else if (e.key === 'ArrowRight') dx = step;
        else if (e.key === 'ArrowUp') dy = -step;
        else if (e.key === 'ArrowDown') dy = step;
        else return;
        e.preventDefault();
        e.stopPropagation();
        onNudge(handle, dx, dy);
    };
    // aria prefix keeps single-mode names unchanged ("Goniometer pivot, ...") and makes
    // dual instances distinguishable ("Left goniometer pivot, ...")
    const ariaPrefix = label === 'L' ? 'Left goniometer' : label === 'R' ? 'Right goniometer' : 'Goniometer';
    const handleA11yProps = (handle: GoniometerHandle, part: string) => ({
        tabIndex: 0,
        role: 'slider' as const,
        'aria-label': `${ariaPrefix} ${part}, arrow keys to move`,
        'aria-valuenow': Math.round(angle),
        'aria-valuemin': 0,
        'aria-valuemax': 180,
        'aria-valuetext': `${Math.round(angle)} degrees`,
        onKeyDown: makeKeyHandler(handle),
    });
    return (
        <svg className="absolute inset-0 w-full h-full pointer-events-auto" style={{ touchAction: 'none' }}>
            <line x1={`${goniometer.pivot.x * 100}%`} y1={`${goniometer.pivot.y * 100}%`} x2={`${goniometer.arm1.x * 100}%`} y2={`${goniometer.arm1.y * 100}%`} stroke={color} strokeOpacity="0.8" strokeWidth={thickness} />
            <line x1={`${goniometer.pivot.x * 100}%`} y1={`${goniometer.pivot.y * 100}%`} x2={`${goniometer.arm2.x * 100}%`} y2={`${goniometer.arm2.y * 100}%`} stroke={color} strokeOpacity="0.8" strokeWidth={thickness} />
            <circle {...handleA11yProps('pivot', 'pivot')} cx={`${goniometer.pivot.x * 100}%`} cy={`${goniometer.pivot.y * 100}%`} r={HANDLE_RADIUS} fill={pivotColor} fillOpacity="0.6" stroke={pivotColor} strokeWidth="2" className="cursor-move focus:outline-none focus-visible:[stroke-width:4px]" style={{ pointerEvents: 'all' }} />
            <circle {...handleA11yProps('arm1', 'first arm')} cx={`${goniometer.arm1.x * 100}%`} cy={`${goniometer.arm1.y * 100}%`} r={HANDLE_RADIUS} fill={color} fillOpacity="0.6" stroke={color} strokeWidth="2" className="cursor-pointer focus:outline-none focus-visible:[stroke-width:4px]" style={{ pointerEvents: 'all' }} />
            <circle {...handleA11yProps('arm2', 'second arm')} cx={`${goniometer.arm2.x * 100}%`} cy={`${goniometer.arm2.y * 100}%`} r={HANDLE_RADIUS} fill={color} fillOpacity="0.6" stroke={color} strokeWidth="2" className="cursor-pointer focus:outline-none focus-visible:[stroke-width:4px]" style={{ pointerEvents: 'all' }} />
            {label && (
                <text
                    x={`${goniometer.pivot.x * 100}%`}
                    y={`${goniometer.pivot.y * 100}%`}
                    dx={-HANDLE_RADIUS - 6}
                    dy={-HANDLE_RADIUS - 6}
                    textAnchor="end"
                    fill={pivotColor}
                    stroke="rgba(0,0,0,0.6)"
                    strokeWidth="0.75"
                    fontSize="16"
                    fontWeight="700"
                    style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                    {label}
                </text>
            )}
        </svg>
    );
};

const VisioMeter2: React.FC<VisioMeter2Props> = ({ isOpen, onClose, formValues, onValueConfirm, onValueChange, initialTest, testDataMap, clientName, videoSourceStream, embedded = false, sidebarExtra, renderMediaOverlay }) => {
    const { settings } = useSettings();
    const availableTestData = testDataMap ?? PASSIVE_TEST_DATA;

    // Test selection and measurement state
    const [selectedTestKey, setSelectedTestKey] = useState<string>('');
    const [selectedSide, setSelectedSide] = useState<'L' | 'R'>('L');
    const [isTestSelected, setIsTestSelected] = useState(false);
    const [showGoniometer, setShowGoniometer] = useState(false);

    // Initialize with provided test and side
    useEffect(() => {
        if (initialTest && isOpen) {
            setSelectedTestKey(initialTest.testKey);
            setSelectedSide(initialTest.side);
            setIsTestSelected(true);
            setShowGoniometer(true);
        }
    }, [initialTest, isOpen]);

    // Media and playback state
    const [status, setStatus] = useState<string | null>(null);
    const [mediaType, setMediaType] = useState<MediaType>('camera');
    const [mediaSource, setMediaSource] = useState<string | MediaStream | null>(null);
    const [isFrozen, setIsFrozen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const { devices: cameraDevices, refresh: refreshCameras, setAvailability: setCameraAvailability } = useCameraDevices();
    const [currentCameraId, setCurrentCameraId] = useState<string>('');

    // Goniometer position state lives in the portable hook (supports 1..n instances);
    // dual-goniometer tests (Foot Presentation) run two, everything else runs one.
    const {
        instances: goniometers,
        active: activeGoniometerHandle,
        reset: resetGoniometers,
        grabAt: grabGoniometerHandle,
        moveActive: moveActiveGoniometerHandle,
        release: releaseGoniometerHandle,
        nudge: nudgeGoniometerHandle,
        flipAll: flipGoniometer,
    } = useGoniometers();
    const [angles, setAngles] = useState<number[]>([0]);
    // Primary instance's angle — the only one in single-goniometer mode
    const angle = angles[0] ?? 0;
    // Announced only on handle release so screen readers are not flooded during drags
    const [announcedAngle, setAnnouncedAngle] = useState<number | null>(null);
    const showNoMediaState = !status && !mediaSource;
    /** Remote Assessment: the shared call window is the only source (no camera, share, upload). */
    const isRemoteSource = videoSourceStream !== undefined;

    // Discoverability: the goniometer appears as soon as a test is selected and media
    // exists — no hidden click-a-side step required
    useEffect(() => {
        if (isTestSelected && mediaSource) {
            setShowGoniometer(true);
        }
    }, [isTestSelected, mediaSource]);

    // Image storage state
    const [storeImage, setStoreImage] = useState(false);

    // Refs
    const videoRef = useRef<HTMLVideoElement>(null);
    // The <video> element as state, so an overlay can follow it (videoRef alone doesn't re-render).
    const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
    const setVideoRef = useCallback((el: HTMLVideoElement | null) => {
        videoRef.current = el;
        setVideoElement(el);
    }, []);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);
    const currentDeviceIndexRef = useRef(0);
    const mediaSourceRef = useRef(mediaSource);
    // True while a press that grabbed a goniometer handle is in flight — suppresses click-to-pause on release
    const wasDraggingRef = useRef(false);
    // Last known playback position — restored after source re-attachment
    const lastVideoTimeRef = useRef(0);
    // Mirror of playbackRate state — video.load() resets the element's rate to 1, so the
    // media-attach effect re-applies the chosen rate without needing playbackRate as a dep
    const playbackRateRef = useRef(1);
    // Which live source Retake should resume after a freeze — 'camera' or 'screen'
    const lastLiveSourceRef = useRef<'camera' | 'screen' | 'remote'>('camera');
    // Screen-share track + its 'ended' listener — stored so we can remove the listener
    // before we voluntarily stop the track (capture / cleanup). Without this, our own
    // track.stop() fires the 'ended' event which re-enters cleanupMedia() and wipes the
    // frozen captured frame, breaking the screen-share capture workflow entirely.
    const screenTrackEndedRef = useRef<{ track: MediaStreamTrack; listener: () => void } | null>(null);
    // Last selected video file descriptor — used to report decode failures with context
    const lastVideoFileRef = useRef<{ name: string; type: string } | null>(null);

    useEffect(() => {
        mediaSourceRef.current = mediaSource;
    }, [mediaSource]);

    // Track viewport to avoid double-rendering media elements (prevents ref conflicts)
    const [isLg, setIsLg] = useState<boolean>(false);
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mql = window.matchMedia('(min-width: 1024px)');
        const handleChange = (e: MediaQueryListEvent) => setIsLg(e.matches);
        setIsLg(mql.matches);
        mql.addEventListener('change', handleChange);
        return () => mql.removeEventListener('change', handleChange);
    }, []);

    // Get current test data
    const currentTestData = useMemo(() => {
        return selectedTestKey ? availableTestData[selectedTestKey] : null;
    }, [availableTestData, selectedTestKey]);

    useEffect(() => {
        if (selectedTestKey && !availableTestData[selectedTestKey]) {
            setSelectedTestKey('');
            setIsTestSelected(false);
            setShowGoniometer(false);
        }
    }, [availableTestData, selectedTestKey]);

    // Dual-goniometer tests (Foot Presentation): two instances (L + R leg) measured at once,
    // photographed from the Front (tibia / first ray) or Back (calcaneal angle). The view
    // replaces the L/R side toggle and suffixes the stored field keys.
    const isDualGoniometer = Boolean(currentTestData?.dualGoniometer);
    const [dualView, setDualView] = useState<'Front' | 'Back'>('Front');
    // All value reads/writes go through this key, so dual measurements land in
    // e.g. L_FootPresentation_Front without touching the category select field.
    const effectiveTestKey = isDualGoniometer && selectedTestKey ? `${selectedTestKey}_${dualView}` : selectedTestKey;

    // Reset goniometer(s) when new media is loaded (or the dual view flips).
    useEffect(() => {
        if (!mediaSource || !showGoniometer) return;
        if (isDualGoniometer) {
            // In the Front (anterior) photo the client's left leg is on the image's RIGHT;
            // the Back (posterior) photo shows it on the left. Seed each near-vertical
            // (limb axis) with a horizontal reference arm pointing inward.
            const makeInstance = (x: number): GoniometerInstance => ({
                pivot: { x, y: 0.55 },
                arm1: { x, y: 0.3 },
                arm2: { x: x < 0.5 ? x + 0.18 : x - 0.18, y: 0.55 },
            });
            const leftLegX = dualView === 'Front' ? 0.62 : 0.38;
            const rightLegX = dualView === 'Front' ? 0.38 : 0.62;
            resetGoniometers([makeInstance(leftLegX), makeInstance(rightLegX)]);
        } else {
            // 90° start: one vertical line, one horizontal line
            resetGoniometers([{
                pivot: { x: 0.5, y: 0.5 },
                arm1: { x: 0.5, y: 0.3 },
                arm2: { x: 0.7, y: 0.5 },
            }]);
        }
    }, [mediaSource, showGoniometer, isDualGoniometer, dualView, resetGoniometers]);

    // Get current measurement value
    const currentValue = useMemo(() => {
        if (!effectiveTestKey || !selectedSide) return null;
        const id = `${selectedSide}_${effectiveTestKey}`;
        const value = formValues[id];
        return value ? parseFloat(String(value)) : null;
    }, [effectiveTestKey, selectedSide, formValues]);

    // Calculate input highlight class based on current test
    const getInputHighlightClass = useCallback((value: number | null): string => {
        if (!currentTestData || value === null) return '';

        // Full-opacity foreground text — the historical /20 and /30 text-opacity suffixes
        // rendered the value nearly invisible on top of the colored fill.
        if (selectedTestKey === 'HipExtension') {
            if (value >= 0) return 'bg-success text-success-foreground border-success';
            else return 'bg-error text-error-foreground border-error';
        } else if (currentTestData.type !== 'text' && currentTestData.type !== 'select') {
            const nominalVal = parseFloat(currentTestData.nominal as string);
            if (isNaN(value) || isNaN(nominalVal) || nominalVal === 0) return '';

            if (value > nominalVal) return 'bg-warning text-warning-foreground';
            else if (value >= nominalVal * 0.85) return 'bg-success text-success-foreground border-success';
            else if (value >= nominalVal * 0.50) return 'bg-warning text-warning-foreground';
            else return 'bg-error text-error-foreground border-error';
        }
        return '';
    }, [currentTestData, selectedTestKey]);

    // The screen 'ended' listener must be detached before WE stop tracks on ANY path —
    // a legitimately-late 'ended' (share ending in the same beat as our action) would
    // otherwise re-enter cleanupMedia() and wipe freshly-set media/frozen state.
    const detachScreenEndedListener = useCallback(() => {
        if (screenTrackEndedRef.current) {
            const { track, listener } = screenTrackEndedRef.current;
            track.removeEventListener('ended', listener);
            screenTrackEndedRef.current = null;
        }
    }, []);

    // Media cleanup
    const cleanupMedia = useCallback(() => {
        detachScreenEndedListener();
        setMediaSource(prevSource => {
            if (prevSource instanceof MediaStream) {
                prevSource.getTracks().forEach(track => track.stop());
            } else if (typeof prevSource === 'string' && prevSource.startsWith('blob:')) {
                URL.revokeObjectURL(prevSource);
            }
            return null;
        });
        setIsFrozen(false);
        setMediaType('camera');
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setPlaybackRate(1);
        lastVideoTimeRef.current = 0;
        // Clear the picker selection so re-choosing the same camera after Stop fires onValueChange
        setCurrentCameraId('');
    }, [detachScreenEndedListener]);

    // Camera setup. Returns success so callers (switch/cycle) can skip past dead cameras
    // instead of stranding the practitioner on an unavailable device.
    const startCamera = useCallback(async (deviceId?: string): Promise<boolean> => {
        cleanupMedia();
        setStatus("Requesting camera access...");
        if (deviceId) setCurrentCameraId(deviceId);
        const constraints = { video: { deviceId: deviceId ? { exact: deviceId } : undefined } } as MediaStreamConstraints;
        logger.debug('VisioMeter: requesting getUserMedia', {
            category: LogCategory.CAMERA,
            camera: true,
            deviceId: deviceId ? 'specific' : 'default',
            constraints: JSON.stringify(constraints),
        });
        try {
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            lastLiveSourceRef.current = 'camera';
            setMediaSource(stream);
            setMediaType('camera');
            setStatus(null);
            if (deviceId) setCameraAvailability(deviceId, 'available');
            // Labels become readable once permission is granted — refresh so the picker fills in
            void refreshCameras();
            logger.info('VisioMeter: camera started successfully', {
                category: LogCategory.CAMERA,
                camera: true,
                trackCount: stream.getTracks().length,
            });
            return true;
        } catch (err) {
            const domErr = err as DOMException;
            logger.error('VisioMeter: camera permission error', {
                category: LogCategory.CAMERA,
                camera: true,
                errorName: domErr?.name,
                errorCode: domErr?.code,
                errorMessage: (err as Error)?.message,
                stack: (err as Error)?.stack?.slice(0, 500),
            });
            // A device-specific failure marks the camera red in the picker; a permission
            // denial is account-level, not a property of this device
            if (deviceId && domErr?.name !== 'NotAllowedError' && domErr?.name !== 'SecurityError') {
                setCameraAvailability(deviceId, 'unavailable');
            }
            setStatus(getCameraErrorMessage(err));
            return false;
        }
    }, [cleanupMedia, refreshCameras, setCameraAvailability]);

    const setupAndStartCamera = useCallback(async () => {
        try {
            let devices = cameraDevices;
            if (devices.length === 0 || devices.every(d => d.availability === 'needs-permission')) {
                logger.debug('VisioMeter: requesting camera permission + enumerating devices', {
                    category: LogCategory.CAMERA,
                    camera: true,
                });
                // Trigger the browser permission prompt; stop the probe stream immediately
                // so the camera LED goes off and the OS handle is released before the real
                // startCamera() call acquires a fresh stream.
                const permStream = await navigator.mediaDevices.getUserMedia({ video: true });
                permStream.getTracks().forEach(t => t.stop());
                devices = await refreshCameras();
                logger.info('VisioMeter: enumerated video devices', {
                    category: LogCategory.CAMERA,
                    camera: true,
                    deviceCount: devices.length,
                    labels: devices.map(d => d.label || '(no label)'),
                });
            }
            if (devices.length === 0) {
                logger.warn('VisioMeter: no camera found', {
                    category: LogCategory.CAMERA,
                    camera: true,
                    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent?.slice(0, 100) : undefined,
                });
                setStatus('No camera found.');
                return;
            }
            const deviceId = devices[currentDeviceIndexRef.current % devices.length]?.deviceId;
            await startCamera(deviceId);
        } catch (err) {
            const domErr = err as DOMException;
            logger.error('VisioMeter: camera setup error', {
                category: LogCategory.CAMERA,
                camera: true,
                errorName: domErr?.name,
                errorCode: domErr?.code,
                errorMessage: (err as Error)?.message,
                stack: (err as Error)?.stack?.slice(0, 500),
            });
            setStatus(getCameraErrorMessage(err));
        }
    }, [cameraDevices, refreshCameras, startCamera]);

    // Share a screen/window/tab (e.g. a Zoom/Teams call showing a remote client) as the
    // measurement source. getDisplayMedia() returns the same MediaStream type getUserMedia()
    // does, so nothing downstream (goniometer, snap, playback) needs to know the difference.
    const startScreenShare = useCallback(async () => {
        cleanupMedia();
        setStatus('Requesting screen share...');
        logger.debug('VisioMeter: requesting getDisplayMedia', {
            category: LogCategory.CAMERA,
            screen: true,
        });
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
            lastLiveSourceRef.current = 'screen';
            setMediaSource(stream);
            setMediaType('screen');
            setStatus(null);
            logger.info('VisioMeter: screen share started successfully', {
                category: LogCategory.CAMERA,
                screen: true,
                trackCount: stream.getTracks().length,
            });
            // Browser's own "Stop sharing" UI ends the track outside our controls —
            // fall back to the no-media state instead of leaving a dead frozen frame.
            const [track] = stream.getVideoTracks();
            if (track) {
                const onScreenTrackEnded = () => {
                    logger.debug('VisioMeter: screen share ended by browser UI', {
                        category: LogCategory.CAMERA,
                        screen: true,
                    });
                    cleanupMedia();
                };
                screenTrackEndedRef.current = { track, listener: onScreenTrackEnded };
                track.addEventListener('ended', onScreenTrackEnded);
            }
        } catch (err) {
            const domErr = err as DOMException;
            // User cancelling the share picker is a normal outcome, not an error.
            if (domErr?.name === 'NotAllowedError') {
                setStatus(null);
                return;
            }
            logger.error('VisioMeter: screen share error', {
                category: LogCategory.CAMERA,
                screen: true,
                errorName: domErr?.name,
                errorMessage: (err as Error)?.message,
            });
            setStatus('Could not start screen share. Try again or use the camera instead.');
            setTimeout(() => setStatus(null), 3000);
        }
    }, [cleanupMedia]);

    const stopCamera = useCallback(() => {
        logger.debug('VisioMeter: stopping camera', {
            category: LogCategory.CAMERA,
            camera: true,
        });
        cleanupMedia();
    }, [cleanupMedia]);

    // Remote Assessment: measure on a fresh clone of the shared call window (no picker, no camera).
    const startRemoteStream = useCallback(() => {
        if (!videoSourceStream) return;
        cleanupMedia();
        lastLiveSourceRef.current = 'remote';
        setMediaSource(videoSourceStream.clone());
        setMediaType('screen');
        setStatus(null);
        logger.debug('VisioMeter: measuring on the Remote Assessment shared call window', {
            category: LogCategory.CAMERA,
            screen: true,
        });
    }, [videoSourceStream, cleanupMedia]);

    const stopScreenShare = useCallback(() => {
        logger.debug('VisioMeter: stopping screen share', {
            category: LogCategory.CAMERA,
            screen: true,
        });
        cleanupMedia();
    }, [cleanupMedia]);

    // Cycle to the next camera (mobile island). Tries each remaining device in order —
    // one dead/in-use camera can no longer trap the practitioner on an error screen.
    const switchCamera = useCallback(async () => {
        const devices = cameraDevices;
        if (devices.length <= 1) {
            logger.debug('VisioMeter: only one camera available, cannot switch', {
                category: LogCategory.CAMERA,
                camera: true,
                deviceCount: devices.length,
            });
            setStatus('Only one camera available.');
            setTimeout(() => setStatus(null), 2000);
            return;
        }
        // Full circle: the "current" index may point at a device that never actually started,
        // so every device is a legitimate candidate (minus ones already marked unavailable)
        for (let attempt = 1; attempt <= devices.length; attempt++) {
            currentDeviceIndexRef.current = (currentDeviceIndexRef.current + 1) % devices.length;
            const candidate = devices[currentDeviceIndexRef.current];
            if (candidate?.availability === 'unavailable') continue;
            if (await startCamera(candidate?.deviceId)) return;
        }
        // All alternatives failed — the last startCamera call left a specific error message up
    }, [cameraDevices, startCamera]);

    // Shared helper: load image from File into media source
    const applyImageFromFile = useCallback((file: File) => {
        const current = mediaSourceRef.current;
        if (current instanceof MediaStream) {
            detachScreenEndedListener();
            current.getTracks().forEach(track => track.stop());
        }
        if (typeof current === 'string' && current.startsWith('blob:')) {
            URL.revokeObjectURL(current);
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const dataUrl = event.target?.result as string;
            if (dataUrl) {
                setMediaSource(dataUrl);
                setMediaType('image');
                setIsFrozen(false);
                setStatus(null);
            }
        };
        reader.onerror = () => {
            setStatus('Failed to load image. Please try another file.');
            setTimeout(() => setStatus(null), 3000);
        };
        reader.readAsDataURL(file);
    }, [detachScreenEndedListener]);

    // File upload handler
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const isImage = file.type.startsWith('image/');
        const isVideo = file.type.startsWith('video/');

        if (!isImage && !isVideo) {
            setStatus('Please select an image or video file.');
            setTimeout(() => setStatus(null), 3000);
            return;
        }

        if (mediaSource instanceof MediaStream) {
            detachScreenEndedListener();
            mediaSource.getTracks().forEach(track => track.stop());
        }

        if (typeof mediaSource === 'string' && mediaSource.startsWith('blob:')) {
            URL.revokeObjectURL(mediaSource);
        }

        if (isImage) {
            applyImageFromFile(file);
        } else {
            lastVideoFileRef.current = { name: file.name, type: file.type };
            // Preflight: canPlayType('') means the browser doesn't advertise support.
            // It under-reports (H.264 .mov plays fine despite ''), so attempt playback
            // anyway — handleVideoError surfaces the failure if it truly can't decode.
            const support = document.createElement('video').canPlayType(file.type || '');
            if (file.type && support === '') {
                logger.warn('VisioMeter: browser reports no support for uploaded video type, attempting playback anyway', {
                    category: LogCategory.CAMERA,
                    fileType: file.type,
                });
            }
            let blobUrl: string;
            try {
                blobUrl = URL.createObjectURL(file);
            } catch (err) {
                logger.error('VisioMeter: could not create object URL for uploaded video', {
                    category: LogCategory.CAMERA,
                    fileSize: file.size,
                    errorMessage: (err as Error)?.message,
                });
                setStatus('Could not open this file. Try a smaller video.');
                setTimeout(() => setStatus(null), 3000);
                return;
            }
            setMediaSource(blobUrl);
            setMediaType('video');
            setIsFrozen(false);
            setIsPlaying(false);
            setCurrentTime(0);
            lastVideoTimeRef.current = 0;
            setStatus(null);
        }

        e.target.value = '';
    };

    // Uploaded video failed to decode (e.g. iPhone HEVC .mov in a browser without
    // HEVC support). Without this the player stays blank with zero feedback.
    const handleVideoError = () => {
        if (mediaType !== 'video') return;
        const mediaError = videoRef.current?.error;
        const file = lastVideoFileRef.current;
        logger.error('VisioMeter: uploaded video failed to load', {
            category: LogCategory.CAMERA,
            errorCode: mediaError?.code,
            errorMessage: mediaError?.message,
            fileType: file?.type,
        });
        if (typeof mediaSource === 'string' && mediaSource.startsWith('blob:')) {
            URL.revokeObjectURL(mediaSource);
        }
        setMediaSource(null);
        setMediaType('camera');
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        lastVideoTimeRef.current = 0;
        setStatus(`Couldn't play "${file?.name ?? 'this video'}". This browser can't decode it — convert it to MP4 (H.264) and try again. iPhone videos (.mov/HEVC) usually need this.`);
        setTimeout(() => setStatus(null), 8000);
    };

    // Clipboard paste handler
    const handlePaste = useCallback((e: ClipboardEvent) => {
        if (!e.clipboardData) return;
        const imageItems = Array.from(e.clipboardData.items).filter(item => item.type.startsWith('image/'));
        if (!imageItems.length) return;
        e.preventDefault();
        const file = imageItems[0].getAsFile();
        if (file) {
            applyImageFromFile(file);
            setStatus('Image pasted');
            setTimeout(() => setStatus(null), 1500);
        }
    }, [applyImageFromFile]);

    // Capture or retake handler
    const handleCaptureOrRetake = () => {
        if (!isFrozen && (mediaType === 'camera' || mediaType === 'screen')) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            if (canvas && video && video.videoWidth > 0) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                canvas.getContext('2d')?.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
                setIsFrozen(true);
                if (mediaSource instanceof MediaStream) {
                    detachScreenEndedListener();
                    mediaSource.getTracks().forEach(track => track.stop());
                }
            }
        } else if (lastLiveSourceRef.current === 'remote' && videoSourceStream) {
            startRemoteStream();
        } else if (lastLiveSourceRef.current === 'screen') {
            startScreenShare();
        } else {
            setupAndStartCamera();
        }
    };

    // Test selection handler
    const handleTestSelect = (testKey: string) => {
        setSelectedTestKey(testKey);
        setSelectedSide('L');
        setDualView('Front');
        setIsTestSelected(true);
        setShowGoniometer(true);
    };

    // Side selection handler
    const handleSideSelect = (side: 'L' | 'R') => {
        setSelectedSide(side);
        setShowGoniometer(true);
    };

    // Presence-aware read of a stored side value. formValues values may be strings OR
    // numbers (saved docs round-trip untyped) — a numeric 0 is a real value (Hip Extension
    // full extension = pass), so truthiness must never decide presence.
    const sideRawValue = (side: 'L' | 'R'): string | null => {
        const raw = formValues[`${side}_${effectiveTestKey}`];
        if (raw === undefined || raw === null || String(raw).trim() === '') return null;
        return String(raw);
    };

    // Deficit-style tests (allowsNegative, e.g. Hip Extension) record short-of-neutral as a
    // negative number — the ± toggle flips the stored value's sign for those tests only.
    // A sign flip changes a scored clinical value in one tap, so it gets a transient Undo.
    const showSignToggle = Boolean(currentTestData?.allowsNegative);
    const [signFlipUndo, setSignFlipUndo] = useState<{ side: 'L' | 'R'; value: number } | null>(null);
    const signFlipUndoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const toggleSign = (side: 'L' | 'R') => {
        if (!effectiveTestKey) return;
        const raw = sideRawValue(side);
        const parsed = raw === null ? NaN : parseFloat(raw);
        if (isNaN(parsed) || parsed === 0) return;
        onValueConfirm(effectiveTestKey, side, -parsed);
        setSignFlipUndo({ side, value: parsed });
        if (signFlipUndoTimerRef.current) clearTimeout(signFlipUndoTimerRef.current);
        signFlipUndoTimerRef.current = setTimeout(() => setSignFlipUndo(null), 6000);
    };
    const undoSignFlip = () => {
        if (!signFlipUndo || !effectiveTestKey) return;
        onValueConfirm(effectiveTestKey, signFlipUndo.side, signFlipUndo.value);
        setSignFlipUndo(null);
        if (signFlipUndoTimerRef.current) clearTimeout(signFlipUndoTimerRef.current);
    };

    // Input handler
    const handleInput = async () => {
        if (!selectedTestKey) return;

        let imageData: string | undefined;
        if (storeImage) {
            // Capture the current viewport as base64 image
            imageData = await captureMeasurementImage();
        }

        const captureSource = captureSourceForMedia(mediaType);
        if (isDualGoniometer) {
            // Both legs measured at once — one Apply stores both angles under the
            // view-suffixed key, each with the stored image and capture source
            confirmMeasurement(onValueConfirm, { testKey: effectiveTestKey, dual: true, angles, imageData, captureSource });
        } else {
            if (!selectedSide) return;
            confirmMeasurement(onValueConfirm, { testKey: effectiveTestKey, dual: false, side: selectedSide, angle, imageData, captureSource });
        }

        // Goniometer stays visible after Apply (the filled/colored input is the completion
        // signal) so measuring the other side is one toggle away, not a hidden input-click
        setStoreImage(false);
    };

    // Export the current annotated view (frame + goniometer arms + angle) as a JPEG
    // download — practitioners drop these straight into client reports, so this is a
    // sibling of storeImage (Firestore attach) built on the same composite.
    // Filename contract (Michail): <Client>-<Test>-<Side>-<yy-m-d>-<hhmm>.jpg so report
    // folders sort by client; the client part is omitted when no client is in context.
    const handleExportImage = async () => {
        const imageData = await captureMeasurementImage();
        if (!imageData) {
            logger.warn('VisioMeter image export produced no image data', { category: LogCategory.UI });
            return;
        }
        const sanitize = (part: string) => part.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const now = new Date();
        const datePart = `${now.getFullYear() % 100}-${now.getMonth() + 1}-${now.getDate()}`;
        const timePart = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
        const parts = [
            clientName ? sanitize(clientName) : '',
            sanitize(currentTestData?.label || selectedTestKey || 'measurement'),
            // A dual-goniometer image shows BOTH legs, so the view (Front/Back) replaces L/R
            isDualGoniometer ? dualView : selectedSide,
            datePart,
            timePart,
        ].filter(Boolean);
        const link = document.createElement('a');
        link.href = imageData;
        link.download = `${parts.join('-')}.jpg`;
        link.click();
    };

    // Capture measurement image
    const captureMeasurementImage = async (): Promise<string> => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const video = videoRef.current;
            const frozenCanvas = canvasRef.current;
            const overlay = overlayRef.current;

            if (!ctx || !overlay) {
                resolve('');
                return;
            }

            const rect = overlay.getBoundingClientRect();

            // Optimize size: max 1200px width while maintaining aspect ratio
            const maxWidth = 1200;
            const scale = Math.min(1, maxWidth / rect.width);
            canvas.width = rect.width * scale;
            canvas.height = rect.height * scale;

            // Helper function to draw final image with proper aspect ratio
            const drawFinalImage = (source: CanvasImageSource) => {
                // Get source dimensions
                const sourceWidth = (source as HTMLImageElement).naturalWidth ||
                    (source as HTMLVideoElement).videoWidth ||
                    (source as HTMLCanvasElement).width;
                const sourceHeight = (source as HTMLImageElement).naturalHeight ||
                    (source as HTMLVideoElement).videoHeight ||
                    (source as HTMLCanvasElement).height;

                if (!sourceWidth || !sourceHeight) {
                    // Fallback: just draw without aspect ratio correction
                    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
                } else {
                    // Calculate aspect ratios
                    const sourceAspect = sourceWidth / sourceHeight;
                    const canvasAspect = canvas.width / canvas.height;

                    let drawWidth = canvas.width;
                    let drawHeight = canvas.height;
                    let offsetX = 0;
                    let offsetY = 0;

                    // Fit image within canvas while maintaining aspect ratio (contain mode)
                    if (sourceAspect > canvasAspect) {
                        // Source is wider - fit to width
                        drawHeight = canvas.width / sourceAspect;
                        offsetY = (canvas.height - drawHeight) / 2;
                    } else {
                        // Source is taller - fit to height
                        drawWidth = canvas.height * sourceAspect;
                        offsetX = (canvas.width - drawWidth) / 2;
                    }

                    // Fill background with black (or transparent)
                    ctx.fillStyle = '#000000';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    // Draw image centered and maintaining aspect ratio
                    ctx.drawImage(source, offsetX, offsetY, drawWidth, drawHeight);
                }

                drawGoniometerOverlay(ctx, canvas.width, canvas.height);
                resolve(canvas.toDataURL('image/jpeg', 0.7));
            };

            // Priority 1: Frozen canvas (camera capture or video frame)
            if (isFrozen && frozenCanvas && frozenCanvas.width > 0) {
                drawFinalImage(frozenCanvas);
            }
            // Priority 2: Live video stream
            else if (video && video.videoWidth > 0 && !isFrozen) {
                drawFinalImage(video);
            }
            // Priority 3: Uploaded image
            else if (mediaType === 'image' && mediaSource && typeof mediaSource === 'string') {
                const img = new Image();
                img.onload = () => drawFinalImage(img);
                img.onerror = () => {
                    // Fallback: just overlay if image fails to load
                    drawGoniometerOverlay(ctx, canvas.width, canvas.height);
                    resolve(canvas.toDataURL('image/jpeg', 0.7));
                };
                img.src = mediaSource;
            }
            // Priority 4: Uploaded video (current frame)
            else if (mediaType === 'video' && video && video.readyState >= 2) {
                drawFinalImage(video);
            }
            // Fallback: Just the goniometer overlay (no background)
            else {
                drawGoniometerOverlay(ctx, canvas.width, canvas.height);
                resolve(canvas.toDataURL('image/jpeg', 0.7));
            }
        });
    };

    // Draw goniometer overlay on canvas — all instances, each labeled in dual mode so an
    // exported both-legs image can never be misread (L vs R is a clinical hazard)
    const drawGoniometerOverlay = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
        goniometers.forEach((instance, index) => {
            const pivotX = instance.pivot.x * width;
            const pivotY = instance.pivot.y * height;
            const arm1X = instance.arm1.x * width;
            const arm1Y = instance.arm1.y * height;
            const arm2X = instance.arm2.x * width;
            const arm2Y = instance.arm2.y * height;

            // Draw lines
            ctx.strokeStyle = settings.goniometerColor;
            ctx.lineWidth = settings.goniometerThickness;
            ctx.beginPath();
            ctx.moveTo(pivotX, pivotY);
            ctx.lineTo(arm1X, arm1Y);
            ctx.moveTo(pivotX, pivotY);
            ctx.lineTo(arm2X, arm2Y);
            ctx.stroke();

            // Draw handles
            ctx.fillStyle = settings.goniometerPivotColor;
            ctx.beginPath();
            ctx.arc(pivotX, pivotY, HANDLE_RADIUS, 0, 2 * Math.PI);
            ctx.fill();

            ctx.fillStyle = settings.goniometerColor;
            ctx.beginPath();
            ctx.arc(arm1X, arm1Y, HANDLE_RADIUS, 0, 2 * Math.PI);
            ctx.fill();

            ctx.beginPath();
            ctx.arc(arm2X, arm2Y, HANDLE_RADIUS, 0, 2 * Math.PI);
            ctx.fill();

            // Draw angle text (side-prefixed when two goniometers share the frame)
            const sidePrefix = isDualGoniometer ? (index === 0 ? 'L ' : 'R ') : '';
            ctx.fillStyle = '#000';
            ctx.font = '24px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(`${sidePrefix}${Math.round(angles[index] ?? 0)}°`, pivotX, pivotY - 30);
        });
    };

    // Goniometer interaction handlers
    const getPointFromEvent = (e: React.MouseEvent | React.TouchEvent): Point | null => {
        if (!overlayRef.current) return null;
        const rect = overlayRef.current.getBoundingClientRect();
        const clientX = 'touches' in e ? (e as React.TouchEvent).touches[0].clientX : (e as React.MouseEvent).clientX;
        const clientY = 'touches' in e ? (e as React.TouchEvent).touches[0].clientY : (e as React.MouseEvent).clientY;
        return {
            x: (clientX - rect.left) / rect.width,
            y: (clientY - rect.top) / rect.height,
        };
    };

    const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
        if (!showGoniometer) return;
        const p = getPointFromEvent(e);
        if (!p || !overlayRef.current) return;
        const rect = overlayRef.current.getBoundingClientRect();
        const grabRadius = Math.max(HANDLE_RADIUS / rect.width, HANDLE_RADIUS / rect.height);
        const grabbed = grabGoniometerHandle(p, grabRadius);
        wasDraggingRef.current = grabbed !== null;
    };

    const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (!activeGoniometerHandle || !showGoniometer) return;
        // Note: preventDefault() removed as it's not needed for mouse events and causes issues with passive listeners
        const p = getPointFromEvent(e);
        if (!p) return;
        moveActiveGoniometerHandle(p);
    };

    const handleMouseUp = () => {
        if (activeGoniometerHandle) setAnnouncedAngle(Math.round(angles[activeGoniometerHandle.instance] ?? 0));
        releaseGoniometerHandle();
    };

    // Video playback handlers
    const togglePlay = useCallback(() => {
        if (videoRef.current) {
            if (videoRef.current.paused) {
                videoRef.current.play();
            } else {
                videoRef.current.pause();
            }
        }
    }, []);

    const handleFrameStep = useCallback((direction: number) => {
        if (videoRef.current) {
            videoRef.current.pause();
            const newTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + direction * FRAME_STEP_AMOUNT));
            videoRef.current.currentTime = newTime;
        }
    }, [duration]);

    // Click/tap on the media surface (not on controls or goniometer handles) toggles playback
    const handleMediaSurfaceClick = (e: React.MouseEvent) => {
        if (mediaType !== 'video') return;
        if (wasDraggingRef.current) {
            wasDraggingRef.current = false;
            return;
        }
        const target = e.target as HTMLElement | null;
        if (target?.closest?.(INTERACTIVE_ELEMENT_SELECTOR)) return;
        togglePlay();
    };

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (videoRef.current) {
            videoRef.current.currentTime = parseFloat(e.target.value);
        }
    };

    const updateTime = useCallback(() => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);
            lastVideoTimeRef.current = videoRef.current.currentTime;
        }
    }, []);

    // Effects
    useEffect(() => {
        if (isOpen) {
            currentDeviceIndexRef.current = 0;
            setStatus(null);
            // Populate the camera picker immediately (enumerateDevices works pre-permission,
            // just with blank labels → devices show as needs-permission). Do NOT auto-start
            // the camera here — getUserMedia() must come from a user gesture on mobile.
            void refreshCameras();
        } else {
            cleanupMedia();
            // Reset state when closing
            setSelectedTestKey('');
            setIsTestSelected(false);
            setShowGoniometer(false);
            setStoreImage(false);
            setDualView('Front');
        }
    }, [isOpen, cleanupMedia, refreshCameras]);

    // Remote Assessment: the shared call window is already granted, so go live at once, exactly
    // once per shared stream (re-running on unrelated renders would discard the live clone).
    // When the share goes away, drop the dead clone.
    const startedRemoteStreamRef = useRef<MediaStream | null>(null);
    useEffect(() => {
        if (!isOpen || videoSourceStream === undefined) {
            startedRemoteStreamRef.current = null;
            return;
        }
        if (videoSourceStream === null) {
            if (startedRemoteStreamRef.current) cleanupMedia();
            startedRemoteStreamRef.current = null;
            return;
        }
        if (startedRemoteStreamRef.current === videoSourceStream) return;
        startedRemoteStreamRef.current = videoSourceStream;
        startRemoteStream();
    }, [isOpen, videoSourceStream, startRemoteStream, cleanupMedia]);

    useEffect(() => {
        if (!isOpen) return;
        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [isOpen, handlePaste]);

    // Keyboard playback shortcuts: Space play/pause, arrows frame-step, Home/End jump.
    // Skipped while focus is on an interactive element so Space still activates buttons,
    // selects, and switches normally.
    useEffect(() => {
        if (!isOpen || mediaType !== 'video') return;
        const isInteractiveTarget = (el: Element | null) =>
            Boolean(el?.closest?.(INTERACTIVE_ELEMENT_SELECTOR));
        const isArrowConsumerTarget = (el: Element | null) =>
            Boolean(el?.closest?.(ARROW_KEY_CONSUMER_SELECTOR));
        const handleKeyDown = (event: KeyboardEvent) => {
            const isSpace = event.code === 'Space' || event.key === ' ';
            // Space activates focused buttons/switches — never hijack it from them.
            // Arrows/Home/End only yield to controls that actually consume them.
            if (isSpace ? isInteractiveTarget(document.activeElement) : isArrowConsumerTarget(document.activeElement)) return;
            if (isSpace) {
                event.preventDefault();
                togglePlay();
            } else if (event.key === 'ArrowLeft') {
                event.preventDefault();
                handleFrameStep(-1);
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                handleFrameStep(1);
            } else if (event.key === 'Home' && videoRef.current) {
                event.preventDefault();
                videoRef.current.currentTime = 0;
            } else if (event.key === 'End' && videoRef.current) {
                event.preventDefault();
                // Read duration from the element so this document-level listener doesn't
                // resubscribe every time the duration state updates
                const mediaDuration = videoRef.current.duration;
                if (Number.isFinite(mediaDuration)) {
                    videoRef.current.currentTime = mediaDuration;
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, mediaType, togglePlay, handleFrameStep]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;
        if (mediaSource instanceof MediaStream) {
            (video as any).srcObject = mediaSource;
            video.muted = true;
            video.play().catch(e => {
                const domErr = e as DOMException;
                logger.error('VisioMeter: camera autoplay failed', {
                    category: LogCategory.CAMERA,
                    camera: true,
                    errorName: domErr?.name,
                    errorMessage: (e as Error)?.message,
                });
                setStatus('Camera preview failed to start. Try Retake or switch camera.');
            });
        } else if (typeof mediaSource === 'string' && mediaType === 'video') {
            (video as any).srcObject = null;
            video.src = mediaSource;
            video.muted = true; // keep muted to allow autoplay without user gesture
            // Ensure browser loads first frame and attempts playback
            try { video.load(); } catch { }
            // load() resets the element's playbackRate to 1 — re-apply the chosen rate
            video.playbackRate = playbackRateRef.current;
            const resumeAt = lastVideoTimeRef.current;
            if (resumeAt > 0) {
                // Restore position after the isLg fork remounted the element mid-playback
                video.addEventListener('loadedmetadata', () => {
                    if (videoRef.current) videoRef.current.currentTime = resumeAt;
                }, { once: true });
            }
            video.play().catch(() => {/* ignore autoplay block */ });
        } else {
            (video as any).srcObject = null;
            video.src = '';
        }
        // Unified tree: the <video> element persists across breakpoint changes, so isLg is
        // deliberately NOT a dependency (re-running would pointlessly reload the source).
    }, [mediaSource, mediaType]);

    useEffect(() => {
        // Prefer pixel-based calculation to avoid distortion from differing width/height scales
        const rect = overlayRef.current?.getBoundingClientRect();
        setAngles(
            goniometers.map(instance =>
                rect
                    ? calculateAnglePx(instance.pivot, instance.arm1, instance.arm2, rect.width, rect.height)
                    : calculateAngle(instance.pivot, instance.arm1, instance.arm2)
            )
        );
    }, [goniometers]);

    // Playwright E2E: allow page.evaluate to set normalized goniometer coords (see test/e2e/lib/visual-goniometer-actions.ts).
    useEffect(() => {
        if (import.meta.env.VITE_PLAYWRIGHT_E2E !== 'true') return;
        type E2EApi = { setGoniometerState: (state: GoniometerInstance) => void };
        const api: E2EApi = { setGoniometerState: (state) => resetGoniometers([state]) };
        (window as Window & { __HARRY_VISIOMETER_E2E__?: E2EApi }).__HARRY_VISIOMETER_E2E__ = api;
        return () => {
            delete (window as Window & { __HARRY_VISIOMETER_E2E__?: E2EApi }).__HARRY_VISIOMETER_E2E__;
        };
    }, [resetGoniometers]);

    useEffect(() => {
        // Dual mode writes only on Apply (both legs at once) — no live single-side echo
        if (isOpen && selectedTestKey && selectedSide && onValueChange && !isDualGoniometer) {
            onValueChange(selectedTestKey, selectedSide, Math.round(angle));
        }
    }, [angle, isOpen, selectedTestKey, selectedSide, onValueChange, isDualGoniometer]);

    useEffect(() => {
        playbackRateRef.current = playbackRate;
        if (videoRef.current) {
            videoRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate]);

    // Explicit measuring-side toggle — replaces "click the readonly L/R input" as the
    // (previously undiscoverable) way to point the goniometer at a side
    const sideToggle = (
        <div className="flex shrink-0 overflow-hidden rounded-md border" role="group" aria-label="Measuring side">
            {(['L', 'R'] as const).map(side => (
                <button
                    key={side}
                    type="button"
                    onClick={() => handleSideSelect(side)}
                    aria-pressed={showGoniometer && selectedSide === side}
                    disabled={!isTestSelected}
                    className={cn(
                        'px-2.5 py-1 text-sm font-semibold transition-colors disabled:opacity-40',
                        showGoniometer && selectedSide === side
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent'
                    )}
                >
                    {side}
                </button>
            ))}
        </div>
    );

    // Dual tests photograph a view (both legs in frame), not a side — the toggle follows
    const viewToggle = (
        <div className="flex shrink-0 overflow-hidden rounded-md border" role="group" aria-label="Measured view">
            {(['Front', 'Back'] as const).map(view => (
                <button
                    key={view}
                    type="button"
                    onClick={() => { setDualView(view); setShowGoniometer(true); }}
                    aria-pressed={showGoniometer && dualView === view}
                    disabled={!isTestSelected}
                    className={cn(
                        'px-2.5 py-1 text-sm font-semibold transition-colors disabled:opacity-40',
                        showGoniometer && dualView === view
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent'
                    )}
                >
                    {view}
                </button>
            ))}
        </div>
    );
    const sideOrViewToggle = isDualGoniometer ? viewToggle : sideToggle;

    const canMeasure = isFrozen || mediaType === 'image' || mediaType === 'video';
    const isLiveSource = mediaType === 'camera' || mediaType === 'screen';
    const isCaptureMode = !isFrozen && isLiveSource;
    // Remote Assessment wording: freeze the live call frame to measure on it, unfreeze to go live.
    const captureLabel = isRemoteSource ? (isCaptureMode ? 'Freeze frame' : 'Unfreeze') : (isCaptureMode ? 'Capture' : 'Retake');
    const captureText = isRemoteSource ? captureLabel : (isCaptureMode ? 'Capture photo' : 'Retake');
    // Actively receiving a screen stream (a frozen frame no longer counts — its tracks are stopped)
    const isScreenLive = mediaType === 'screen' && mediaSource instanceof MediaStream && !isFrozen;
    // Actively receiving a camera stream — gates the Stop-camera control (privacy: the camera
    // light must be stoppable without closing the whole modal)
    const isCameraLive = mediaType === 'camera' && mediaSource instanceof MediaStream && !isFrozen;
    // Capture needs an actual stream (or a frozen frame to retake) — mediaType defaults to
    // 'camera' before any stream exists, so type alone must not enable the button
    const canCaptureOrRetake = showGoniometer && (isFrozen || mediaSource instanceof MediaStream);
    // Keep goni overlay hidden until media exists so it cannot block camera-start CTA.
    const shouldRenderGoniometerOverlay = showGoniometer && Boolean(mediaSource);
    // getDisplayMedia is desktop-only in practice (mobile support is inconsistent) — feature-detect
    // rather than assume, so the button simply doesn't appear where it wouldn't work.
    const isDisplayMediaSupported = typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getDisplayMedia === 'function';

    return (
        <EmbeddableFrame embedded={embedded} isOpen={isOpen} onClose={onClose}>
            <div className={cn('flex min-h-0 flex-1 flex-col gap-3 overflow-hidden', embedded ? 'h-full' : 'p-4 sm:gap-4 sm:p-6')}>
                <span className="sr-only" role="status">
                    {announcedAngle !== null ? `Goniometer angle ${announcedAngle} degrees` : ''}
                </span>
                {/* Main Content Area — ONE responsive tree (no isLg fork): the values region is the
                    top card on mobile and the right sidebar on lg+; the media pane renders exactly once,
                    so it can never drift between breakpoints or lose its video on resize. */}
                <div className="flex min-h-0 flex-1 flex-col gap-2 lg:flex-row lg:gap-4">
                    {/* Values region — mobile: shrink-[999] absorbs vertical squeeze (scrolling internally)
                        before the video pane gives up height; lg+: fixed-width right sidebar */}
                    <div className="flex min-h-12 shrink-[999] flex-col gap-2 overflow-y-auto overflow-x-hidden [scrollbar-width:thin] lg:order-2 lg:min-h-0 lg:w-80 lg:shrink-0 lg:gap-3" data-testid="vgon-values-region">
                        <Card className="shrink-0">
                            <CardContent className="space-y-2 px-3 py-2 sm:px-4 lg:space-y-3 lg:py-4">
                                <div className="space-y-2">
                                    <Label htmlFor="test-select" className="hidden lg:block">Select Test</Label>
                                    <div className="flex items-center gap-2">
                                        <Select value={selectedTestKey || undefined} onValueChange={handleTestSelect}>
                                            <SelectTrigger id="test-select" className="h-9 min-w-0 flex-1 lg:h-10" aria-label="Test">
                                                <SelectValue placeholder="Choose a test..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(availableTestData).map(([key, testData]) => (
                                                    <SelectItem key={key} value={key}>
                                                        {testData.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <span className="hidden lg:flex">{sideOrViewToggle}</span>
                                    </div>
                                </div>
                                {isTestSelected && currentTestData && (
                                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1.5 lg:gap-2">
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-1.5">
                                            <label className="text-[10px] uppercase text-muted-foreground lg:text-xs lg:normal-case">Left</label>
                                            {showSignToggle && (
                                                <Button type="button" variant="ghost" size="icon" className="h-5 w-6 shrink-0 px-0 text-xs text-muted-foreground hover:text-foreground" onClick={() => toggleSign('L')} title="Toggle sign" aria-label="Toggle sign of left value">±</Button>
                                            )}
                                        </div>
                                        <Input
                                            type="number"
                                            value={sideRawValue('L') ?? ''}
                                            className={cn('mt-1 h-9 px-1 text-center text-base font-bold md:text-base lg:h-11 lg:text-xl', getInputHighlightClass(sideRawValue('L') !== null ? parseFloat(sideRawValue('L') as string) : null))}
                                            placeholder={currentTestData.unit || ''}
                                            onClick={() => handleSideSelect('L')}
                                            readOnly
                                        />
                                    </div>
                                    {/* max-w keeps a long nominalDisplay from starving the L/R cells */}
                                    <div className="flex h-full max-w-[5.5rem] flex-col items-center justify-center px-1 text-center lg:max-w-24">
                                        <p className="text-[10px] uppercase text-muted-foreground lg:text-xs lg:tracking-wider">Goal</p>
                                        <p className="mt-1 text-xs font-bold leading-tight lg:text-sm lg:leading-snug">{currentTestData.nominalDisplay || (currentTestData.nominal + (currentTestData.unit || ''))}</p>
                                    </div>
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-1.5">
                                            <label className="text-[10px] uppercase text-muted-foreground lg:text-xs lg:normal-case">Right</label>
                                            {showSignToggle && (
                                                <Button type="button" variant="ghost" size="icon" className="h-5 w-6 shrink-0 px-0 text-xs text-muted-foreground hover:text-foreground" onClick={() => toggleSign('R')} title="Toggle sign" aria-label="Toggle sign of right value">±</Button>
                                            )}
                                        </div>
                                        <Input
                                            type="number"
                                            value={sideRawValue('R') ?? ''}
                                            className={cn('mt-1 h-9 px-1 text-center text-base font-bold md:text-base lg:h-11 lg:text-xl', getInputHighlightClass(sideRawValue('R') !== null ? parseFloat(sideRawValue('R') as string) : null))}
                                            placeholder={currentTestData.unit || ''}
                                            onClick={() => handleSideSelect('R')}
                                            readOnly
                                        />
                                    </div>
                                    {signFlipUndo && (
                                        <button
                                            type="button"
                                            onClick={undoSignFlip}
                                            className="col-span-3 mt-1 w-full text-center text-xs text-muted-foreground underline hover:text-foreground"
                                        >
                                            {`${signFlipUndo.side} sign changed to ${-signFlipUndo.value} — Undo`}
                                        </button>
                                    )}
                                    </div>
                                )}
                                {/* Live measurement: angle + Apply (mobile uses the thumb-zone footer) */}
                                <div className="hidden items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 lg:flex">
                                    <span data-testid="visio-meter-angle-readout" className={cn('flex-1 text-center font-bold tabular-nums', isDualGoniometer ? 'text-lg' : 'text-2xl', showGoniometer ? getInputHighlightClass(angle) : 'text-muted-foreground')}>
                                        {showGoniometer
                                            ? isDualGoniometer
                                                ? `L ${Math.round(angles[0] ?? 0)}° · R ${Math.round(angles[1] ?? 0)}°`
                                                : `${Math.round(angle)}°`
                                            : '—'}
                                    </span>
                                    <Button onClick={handleInput} disabled={!canMeasure} size="sm" className="h-8 px-3 text-sm font-semibold" aria-label="Apply measurement">
                                        <CheckIcon className="mr-1 size-3.5 shrink-0" aria-hidden />
                                        Apply
                                    </Button>
                                </div>
                                {/* Goniometer tools under the measured value — flip + store-image icon toggle */}
                                <div className="hidden items-center justify-center gap-2 lg:flex">
                                    <Button variant="outline" size="icon" className="h-9 w-9" onClick={flipGoniometer} disabled={!showGoniometer} title="Flip goniometer" aria-label="Flip goniometer horizontally">
                                        <FlipIcon />
                                    </Button>
                                    <Button
                                        variant={storeImage ? 'default' : 'outline'}
                                        size="icon"
                                        className="h-9 w-9"
                                        onClick={() => setStoreImage(v => !v)}
                                        disabled={!showGoniometer}
                                        aria-pressed={storeImage}
                                        title={storeImage ? 'Image will be stored with the value' : 'Store image with value: off'}
                                        aria-label="Store image with measurement"
                                    >
                                        <PhotoIcon />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-9 w-9"
                                        onClick={handleExportImage}
                                        disabled={!showGoniometer}
                                        title="Download image with measurement"
                                        aria-label="Export image with measurement"
                                    >
                                        <DownloadIcon />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                        {/* Media sources — lg+ only; mobile keeps the floating island on the media pane */}
                        <Card className="hidden shrink-0 lg:block">
                            <CardContent className="space-y-2 px-4 py-4">
                                {!isRemoteSource && (
                                <Label htmlFor="file-upload-desktop" className="m-0 block cursor-pointer">
                                    <Button variant="outline" className="w-full justify-start gap-2 cursor-pointer" asChild>
                                        <span>
                                            <UploadIcon />
                                            Upload image or video
                                            <input id="file-upload-desktop" type="file" className="hidden" accept="image/*,video/*" onChange={handleFileChange} />
                                        </span>
                                    </Button>
                                </Label>
                                )}
                                {isDisplayMediaSupported && !isRemoteSource && (
                                    <Button
                                        variant="outline"
                                        className={cn('w-full justify-start gap-2', isScreenLive && 'border-error text-error hover:bg-error hover:text-error-foreground')}
                                        onClick={isScreenLive ? stopScreenShare : startScreenShare}
                                        aria-label={isScreenLive ? 'Stop sharing screen' : 'Share a screen, window, or tab (e.g. a Zoom or Teams call)'}
                                    >
                                        {isScreenLive ? <ScreenShareOff /> : <ScreenShareIcon />}
                                        {isScreenLive ? 'Stop sharing' : 'Share screen / call'}
                                    </Button>
                                )}
                                {/* Camera picker — ALWAYS visible when devices exist (a failed camera must never
                                    strand the practitioner without a way to pick a different one) */}
                                {cameraDevices.length > 0 && !isRemoteSource && (
                                    <div className="flex items-center gap-2">
                                        <Select
                                            value={currentCameraId || undefined}
                                            onValueChange={(id) => {
                                                if (id.startsWith('camera-placeholder-')) {
                                                    // Pre-permission deviceIds are blank — selecting one IS the permission prompt
                                                    void setupAndStartCamera();
                                                } else {
                                                    void startCamera(id);
                                                }
                                            }}
                                        >
                                            <SelectTrigger aria-label="Select camera" className="h-10 min-w-0 flex-1">
                                                <SelectValue placeholder="Select camera..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {cameraDevices.map((cam, index) => (
                                                    <SelectItem key={cam.deviceId || `placeholder-${index}`} value={cam.deviceId || `camera-placeholder-${index}`}>
                                                        <span className="flex items-center gap-2">
                                                            <span aria-hidden className={cn('inline-block h-2 w-2 shrink-0 rounded-full', cameraDotClass(cam.availability))} />
                                                            {cam.label}
                                                            <span className="sr-only">{cameraAvailabilityText(cam.availability)}</span>
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {isCameraLive && (
                                            <Button
                                                variant="outline"
                                                size="icon"
                                                className="h-10 w-10 shrink-0 border-error text-error hover:bg-error hover:text-error-foreground"
                                                onClick={stopCamera}
                                                title="Stop camera"
                                                aria-label="Stop camera"
                                            >
                                                <StopSquare className="fill-current" />
                                            </Button>
                                        )}
                                    </div>
                                )}
                                {(isLiveSource || isFrozen) && (
                                    <Button variant={isCaptureMode ? 'default' : 'outline'} className="w-full justify-start gap-2" onClick={handleCaptureOrRetake} disabled={!canCaptureOrRetake} aria-label={captureLabel}>
                                        {isCaptureMode ? <CameraIcon /> : <RefreshIcon />}
                                        {captureText}
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                        {sidebarExtra}
                    </div>

                    {/* Media + playback stack — mobile: basis-[45dvh] creates the squeeze deficit the values
                        region absorbs first; lg+: flexible left column */}
                    <div className="flex min-h-0 grow shrink basis-[45dvh] flex-col gap-2 overflow-y-auto overflow-x-hidden [scrollbar-width:thin] lg:order-1 lg:flex-1 lg:basis-0 lg:gap-3" data-testid="vgon-media-stack">
                            {/* Video/Canvas pane — single instance for all breakpoints */}
                            <div className="relative flex min-h-[min(240px,35dvh)] w-full flex-1 items-center justify-center overflow-hidden rounded-lg border bg-muted/50 lg:min-h-[min(280px,40dvh)]"
                                ref={overlayRef}
                                data-testid="visio-meter-overlay"
                                onClick={handleMediaSurfaceClick}
                                onMouseDown={handleMouseDown}
                                onTouchStart={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onTouchMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                                onTouchEnd={handleMouseUp}
                                style={{ touchAction: showGoniometer ? 'none' : 'auto' }}>
                                {status && <p className="text-muted-foreground text-lg">{status}</p>}
                                {showNoMediaState && (
                                    <div className="absolute inset-0 flex items-center justify-center p-3 text-center lg:p-8">
                                        <div className="max-w-md space-y-2 lg:space-y-4">
                                            <div className="hidden text-6xl lg:block">📸</div>
                                            <h3 className="text-base font-semibold lg:text-xl">No Media Loaded</h3>
                                            <p className="text-xs text-muted-foreground lg:text-sm">
                                                {isTestSelected
                                                    ? 'Test selected. Drag the goniometer handles after media loads.'
                                                    : 'Select a test first, then start camera or upload media.'}
                                            </p>
                                            {/* The viewport stays a viewport — sources live in the sidebar (lg+) / strip (mobile) */}
                                            {isRemoteSource ? (
                                                <p className="text-xs text-muted-foreground lg:text-sm">Waiting for the shared call window.</p>
                                            ) : (
                                            <p className="text-xs text-muted-foreground lg:text-sm">
                                                Use the <span className="hidden lg:inline">panel on the right</span><span className="lg:hidden">buttons below</span> to start a camera, share a screen, or upload.
                                            </p>
                                            )}
                                            <p className="hidden text-sm text-muted-foreground lg:block">
                                                You can also paste an image (Ctrl+V / Cmd+V).
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {!status && (
                                    <>
                                        <video
                                            ref={setVideoRef}
                                            playsInline
                                            autoPlay
                                            muted
                                            loop
                                            onPlay={() => setIsPlaying(true)}
                                            onPause={() => setIsPlaying(false)}
                                            onTimeUpdate={updateTime}
                                            onSeeked={updateTime}
                                            onLoadedMetadata={() => {
                                                const video = videoRef.current;
                                                const mediaDuration = video?.duration ?? 0;
                                                setDuration(Number.isFinite(mediaDuration) ? mediaDuration : 0);
                                                // Support breadcrumb: dimensions/duration make silent playback issues diagnosable
                                                logger.debug('VisioMeter: media metadata loaded', {
                                                    category: LogCategory.CAMERA,
                                                    duration: mediaDuration,
                                                    videoWidth: video?.videoWidth,
                                                    videoHeight: video?.videoHeight,
                                                });
                                                if (mediaType === 'video' && (!Number.isFinite(mediaDuration) || mediaDuration <= 0)) {
                                                    logger.warn('VisioMeter: video has no readable duration — seeking will be limited', {
                                                        category: LogCategory.CAMERA,
                                                    });
                                                }
                                            }}
                                            onError={handleVideoError}
                                            className={`max-w-full max-h-full object-contain ${isLiveSource || mediaType === 'video' ? '' : 'hidden'}`}
                                            style={{ pointerEvents: 'none' }}
                                        />
                                        {mediaType === 'image' && <img src={mediaSource as string} className="h-auto max-h-full w-auto max-w-full object-contain" alt="Uploaded content for analysis" style={{ pointerEvents: 'none' }} />}
                                        <canvas ref={canvasRef} className={`absolute inset-0 w-full h-full object-contain ${isFrozen && isLiveSource ? '' : 'hidden'}`} style={{ pointerEvents: 'none' }} />
                                        {renderMediaOverlay?.(videoElement)}
                                        {shouldRenderGoniometerOverlay && goniometers.map((instance, index) => (
                                            <GoniometerOverlay
                                                key={index}
                                                goniometer={instance}
                                                angle={angles[index] ?? 0}
                                                label={isDualGoniometer ? (index === 0 ? 'L' : 'R') : undefined}
                                                color={settings.goniometerColor}
                                                pivotColor={settings.goniometerPivotColor}
                                                thickness={settings.goniometerThickness}
                                                onNudge={(handle, dx, dy) => nudgeGoniometerHandle(index, handle, dx, dy)}
                                            />
                                        ))}
                                    </>
                                )}
                            </div>

                            {/* Mobile tool strip — in-flow BELOW the pane (never overlaying the client),
                                showing only the controls relevant to the current state */}
                            {(mediaSource || isFrozen || mediaType === 'camera') && (
                                <div className="flex shrink-0 flex-wrap items-center justify-center gap-1.5 lg:hidden">
                                    {!isCameraLive && !isScreenLive && !isRemoteSource && (
                                        <Button
                                            onClick={() => {
                                                currentDeviceIndexRef.current = 0;
                                                void setupAndStartCamera();
                                            }}
                                            variant="default"
                                            size="icon"
                                            className="h-11 w-11 rounded-full"
                                            title="Start camera"
                                            aria-label="Start camera"
                                        >
                                            <VideoCameraIcon />
                                        </Button>
                                    )}
                                    {isDisplayMediaSupported && !isCameraLive && !isScreenLive && !isRemoteSource && (
                                        <Button onClick={startScreenShare} variant="outline" size="icon" className="h-11 w-11 rounded-full" title="Share screen or call" aria-label="Share a screen, window, or tab (e.g. a Zoom or Teams call)">
                                            <ScreenShareIcon />
                                        </Button>
                                    )}
                                    {(isLiveSource || isFrozen) && mediaSource !== null && (
                                        <Button onClick={handleCaptureOrRetake} variant={isCaptureMode ? 'default' : 'outline'} size="icon" className="h-11 w-11 rounded-full" disabled={!canCaptureOrRetake} title={captureLabel} aria-label={captureLabel}>
                                            {isCaptureMode ? <CameraIcon /> : <RefreshIcon />}
                                        </Button>
                                    )}
                                    {/* No live-stream requirement — a failed camera start must leave this
                                        escape hatch visible so the user can cycle past the dead device */}
                                    {mediaType === 'camera' && cameraDevices.length > 1 && (
                                        <Button onClick={switchCamera} variant="outline" size="icon" className="h-11 w-11 rounded-full" title="Switch camera" aria-label="Switch camera">
                                            <SwitchCameraIcon />
                                        </Button>
                                    )}
                                    {isCameraLive && (
                                        <Button onClick={stopCamera} variant="outline" size="icon" className="h-11 w-11 rounded-full border-error text-error hover:bg-error hover:text-error-foreground" title="Stop camera" aria-label="Stop camera">
                                            <StopSquare className="fill-current" />
                                        </Button>
                                    )}
                                    {isScreenLive && (
                                        <Button onClick={stopScreenShare} variant="outline" size="icon" className="h-11 w-11 rounded-full border-error text-error hover:bg-error hover:text-error-foreground" title="Stop sharing" aria-label="Stop sharing screen">
                                            <ScreenShareOff />
                                        </Button>
                                    )}
                                    {canMeasure && (
                                        <Button onClick={flipGoniometer} variant="outline" size="icon" className="h-11 w-11 rounded-full" disabled={!showGoniometer} title="Flip goniometer" aria-label="Flip goniometer horizontally">
                                            <FlipIcon />
                                        </Button>
                                    )}
                                    {canMeasure && (
                                        <Button
                                            variant={storeImage ? 'default' : 'outline'}
                                            size="icon"
                                            className="h-11 w-11 rounded-full"
                                            onClick={() => setStoreImage(v => !v)}
                                            disabled={!showGoniometer}
                                            aria-pressed={storeImage}
                                            title={storeImage ? 'Image will be stored with the value' : 'Store image with value: off'}
                                            aria-label="Store image with measurement"
                                        >
                                            <PhotoIcon />
                                        </Button>
                                    )}
                                    {canMeasure && (
                                        <Button
                                            onClick={handleExportImage}
                                            variant="outline"
                                            size="icon"
                                            className="h-11 w-11 rounded-full"
                                            disabled={!showGoniometer}
                                            title="Download image with measurement"
                                            aria-label="Export image with measurement"
                                        >
                                            <DownloadIcon />
                                        </Button>
                                    )}
                                    {/* Upload rides along whenever no live stream is running — including the
                                        initial no-media state, so mobile always starts with Upload + camera options */}
                                    {!isCameraLive && !isScreenLive && (
                                        <Label htmlFor="file-upload-strip-mobile" className="m-0 cursor-pointer">
                                            <Button variant="outline" size="icon" className="h-11 w-11 rounded-full cursor-pointer" asChild title="Upload image or video" aria-label="Upload image or video">
                                                <span>
                                                    <UploadIcon />
                                                    <input id="file-upload-strip-mobile" type="file" className="hidden" accept="image/*,video/*" onChange={handleFileChange} />
                                                </span>
                                            </Button>
                                        </Label>
                                    )}
                                </div>
                            )}

                            {/* Video Playback Controls */}
                            {mediaType === 'video' && (
                                <PlaybackControls
                                    isPlaying={isPlaying}
                                    currentTime={currentTime}
                                    duration={duration}
                                    playbackRate={playbackRate}
                                    onTogglePlay={togglePlay}
                                    onSeek={handleSeek}
                                    onFrameStep={handleFrameStep}
                                    onRateChange={setPlaybackRate}
                                    compact={!isLg}
                                />
                            )}
                            </div>

                            </div>

                    {/* Thumb-zone footer (mobile only): current angle + Apply */}
                    <div className="flex shrink-0 items-center gap-3 border-t border-border/60 bg-background pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom,0px))] lg:hidden">
                                {sideOrViewToggle}
                                <span data-testid="visio-meter-angle-readout" className={cn('flex-1 text-center font-bold tabular-nums', isDualGoniometer ? 'text-lg' : 'text-2xl', showGoniometer ? getInputHighlightClass(angle) : 'text-muted-foreground')}>
                                    {showGoniometer
                                        ? isDualGoniometer
                                            ? `L ${Math.round(angles[0] ?? 0)}° · R ${Math.round(angles[1] ?? 0)}°`
                                            : `${Math.round(angle)}°`
                                        : '—'}
                                </span>
                                <Button
                                    type="button"
                                    className="h-10 touch-manipulation px-5 text-sm font-semibold"
                                    onClick={handleInput}
                                    disabled={!canMeasure}
                                    aria-label="Apply measurement"
                                >
                                    <CheckIcon className="mr-1.5 size-4 shrink-0" aria-hidden />
                                    Apply
                                </Button>
                    </div>
            </div>
        </EmbeddableFrame>
    );
};

/** Full-screen modal normally; a plain full-height block when embedded in the Remote workbench. */
function EmbeddableFrame({ embedded, isOpen, onClose, children }: { embedded: boolean; isOpen: boolean; onClose: () => void; children: React.ReactNode }) {
    if (embedded) return <div className="flex h-full min-h-0 flex-col">{children}</div>;
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="VisioMeter 3.0" maxWidth="w-full h-full max-w-none" padding={false}>
            {children}
        </Modal>
    );
}

export default VisioMeter2;
